import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";

import {
  MapPin,
  Star,
  Image as ImageIcon,
  Plus,
  X,
  Loader2,
  AlertCircle,
  Clock,
  UploadCloud,
  ToggleLeft,
  ToggleRight,
  Users,
  SlidersHorizontal,
  Sun,
  Sunset,
  ChevronDown,
  ChevronUp,
  Check,
  Ship,
  Car,
  Bed,
  Lock,
  Pencil,
  Trash2,
  ArrowLeft,
  PackagePlus,
  Copy,
  CalendarIcon,
  GripVertical,
  Download,
} from "lucide-react";

import { useDragReorder } from "@/lib/useDragReorder";
import { saveItemOrder } from "@/services/reorderService";

import { format } from "date-fns";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TERMINAL_GROUPS } from "@/utils/terminalGroups";
import MatrixPriceEditor, {
  COURSE_DAY_TYPES,
} from "@/components/admin/Matrixpriceeditor";
import { IMAGE_ACCEPT, isImageFile } from "@/lib/uploadImage";
/**
 * Validation rules — kept outside the component so they're testable in
 * isolation and so the "what counts as valid" logic isn't tangled with render.
 */
function validateCourseForm(form) {
  const errors = {};

  // --- Name ---
  const name = form.name.trim();
  if (!name) errors.name = "Course name is required.";
  else if (name.length < 3)
    errors.name = "Course name must be at least 3 characters.";
  else if (name.length > 120)
    errors.name = "Course name must be under 120 characters.";

  // --- Location ---
  if (!form.city.trim()) errors.city = "City is required.";
  if (!form.country.trim()) errors.country = "Country is required.";

  // --- Description ---
  const desc = form.description.trim();
  if (!desc) errors.description = "Description is required.";
  else if (desc.length < 20)
    errors.description =
      "Description should be at least 20 characters — a one-liner won't help golfers decide.";
  else if (desc.length > 2000)
    errors.description = "Description must be under 2000 characters.";

  // --- Price ---
  const price = Number(form.price);
  if (form.price === "" || form.price === null) {
    errors.price = "Price is required.";
  } else if (Number.isNaN(price)) {
    errors.price = "Price must be a number.";
  } else if (price <= 0) {
    errors.price = "Price must be greater than 0.";
  } else if (price > 100000) {
    errors.price =
      "Price looks wrong — over SGD 100,000 per player. Double-check this.";
  }

  // --- Rating ---
  if (form.rating !== "") {
    const rating = Number(form.rating);
    if (Number.isNaN(rating) || rating < 0 || rating > 5) {
      errors.rating = "Rating must be between 0 and 5.";
    }
  }

  // --- Main image ---
  if (!form.image.trim()) {
    errors.image = "A main course image is required.";
  }

  // --- Fees included ---
  const emptyFees = form.fees_included.some((f) => !f.trim());
  if (form.fees_included.length > 0 && emptyFees) {
    errors.fees_included = "Remove empty fee entries before saving.";
  }

  // --- Amenities ---
  const emptyAmenities = form.amenities.some((a) => !a.trim());
  if (form.amenities.length > 0 && emptyAmenities) {
    errors.amenities = "Remove empty amenity entries before saving.";
  }

  // --- Tee time slots ---
  const enabledSlots = form.tee_slots.filter((s) => s.enabled);
  if (enabledSlots.length === 0) {
    errors.tee_slots =
      "At least one tee time slot must stay enabled — a course with none enabled can't be booked.";
  } else {
    const badCapacity = enabledSlots.find(
      (s) => !Number.isInteger(Number(s.capacity)) || Number(s.capacity) <= 0,
    );
    if (badCapacity) {
      errors.tee_slots = `The ${badCapacity.label} slot needs a capacity greater than 0.`;
    }
  }

  return errors;
}

function stripUndefined(value) {
  if (Array.isArray(value)) return value.map(stripUndefined);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)]),
    );
  }
  return value;
}

async function downloadImage(url, filename = "image.jpg") {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("Failed to fetch image");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Download failed, opening image in a new tab instead:", err);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function filenameFromUrl(url, fallback) {
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const last = path.split("/").pop();
    return last || fallback;
  } catch {
    return fallback;
  }
}

// Matches the field order the form is laid out in, top to bottom — so when
// several fields are invalid at once, we jump to whichever one the golfer
// would actually reach first while scrolling down the page.
const FIELD_ORDER = [
  "name",
  "city",
  "country",
  "description",
  "price",
  "rating",
  "image",
  "fees_included",
  "amenities",
  "tee_slots",
];

function scrollToFirstError(errors) {
  const firstField = FIELD_ORDER.find((f) => errors[f]);
  if (!firstField) return;
  const el = document.querySelector(`[data-field="${firstField}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.querySelector("input, select, textarea")?.focus({ preventScroll: true });
}

/* ---------- fixed slot grid (platform-wide, not admin-editable) ---------- */

const SLOT_INTERVAL_MINUTES = 15;
// 7:00 AM – 11:45 AM, and 12:00 PM – 2:30 PM. Fixed by the platform.
const SLOT_WINDOWS = [
  { period: "AM", start: 7 * 60, end: 11 * 60 + 45 },
  { period: "PM", start: 12 * 60, end: 14 * 60 + 30 },
];

function formatTime12(totalMinutes) {
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  const period = hh >= 12 ? "PM" : "AM";
  const hour12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${hour12}:${String(mm).padStart(2, "0")} ${period}`;
}

function formatTime24(totalMinutes) {
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Generates the fixed 31-slot definitions (time + label + period), with
 * no enabled/capacity state — those get layered on per-course. */
function generateSlotDefinitions() {
  const defs = [];
  for (const window of SLOT_WINDOWS) {
    for (let m = window.start; m <= window.end; m += SLOT_INTERVAL_MINUTES) {
      defs.push({
        time: formatTime24(m),
        label: formatTime12(m),
        period: window.period,
      });
    }
  }
  return defs;
}

const SLOT_DEFINITIONS = generateSlotDefinitions(); // 31 slots, computed once

const DEFAULT_CAPACITY = 20;

function createDefaultSlots() {
  return SLOT_DEFINITIONS.map((def) => ({
    ...def,
    enabled: true,
    capacity: DEFAULT_CAPACITY,
  }));
}

function createEmptyForm() {
  return {
    name: "",
    address: "",
    city: "",
    country: "",
    description: "",
    price: "",
    rating: "",
    image: "",
    difficulty: "",
    blocked_advance: 48,
    images: [],
    fees_included: [],
    amenities: [],
    tee_slots: createDefaultSlots(),
    ferry_route_ids: [],
    transport_vehicle_ids: [],
    accommodation_hotel_ids: [],
    block_other_round_times: false,
    block_dates_enabled: false,
    blocked_dates: [],
    terminal_group: "none",
    // Pricing plan: course-level vehicle override + the matrix pricing grid
    // (day-type × player-count × round-count). See MatrixPriceEditor.jsx.
    vehicle_free_for_one_round: false,
    holiday_dates: [], // string[] 'yyyy-MM-dd' — just marks which dates are holidays
    pricing_matrix: {}, // { [dayType]: { p1to2: {r1,r2,r3plus}, p3plus: {...} } }
  };
}

function courseToForm(course) {
  return {
    name: course.name ?? "",
    address: course.address ?? "",
    city: course.city ?? "",
    country: course.country ?? "",
    description: course.description ?? "",
    price: course.price != null ? String(course.price) : "",
    rating: course.rating != null ? String(course.rating) : "",
    image: course.image ?? "",
    difficulty: course.difficulty ?? "",
    blocked_advance: course.blocked_advance ?? 48,
    images: Array.isArray(course.images) ? course.images : [],
    fees_included: Array.isArray(course.fees_included)
      ? course.fees_included
      : [],
    amenities: Array.isArray(course.amenities) ? course.amenities : [],
    tee_slots:
      Array.isArray(course.tee_slots) && course.tee_slots.length > 0
        ? SLOT_DEFINITIONS.map((def) => {
            const saved = course.tee_slots.find((s) => s.time === def.time);
            return {
              ...def,
              enabled: saved ? Boolean(saved.enabled) : true,
              capacity: saved?.capacity ?? DEFAULT_CAPACITY,
            };
          })
        : createDefaultSlots(),
    ferry_route_ids: Array.isArray(course.ferry_route_ids)
      ? course.ferry_route_ids
      : [],
    transport_vehicle_ids: Array.isArray(course.transport_vehicle_ids)
      ? course.transport_vehicle_ids
      : [],
    accommodation_hotel_ids: Array.isArray(course.accommodation_hotel_ids)
      ? course.accommodation_hotel_ids
      : [],
    block_other_round_times: Boolean(course.block_other_round_times),
    block_dates_enabled: Boolean(course.block_dates_enabled),
    blocked_dates: Array.isArray(course.blocked_dates)
      ? course.blocked_dates
      : [],
    terminal_group: course.terminal_group ?? "none",
    vehicle_free_for_one_round: Boolean(course.vehicle_free_for_one_round),
    holiday_dates: Array.isArray(course.holiday_dates)
      ? course.holiday_dates
      : // Legacy docs from the per-date-price model — keep the dates, the
        // old per-date prices don't carry over (the matrix's holiday_am/pm
        // cells need filling in directly).
        (Array.isArray(course.public_holiday_dates)
          ? course.public_holiday_dates
          : Array.isArray(course.public_holidays)
            ? course.public_holidays.map((h) => h.date)
            : []),
    pricing_matrix:
      course.pricing_matrix && typeof course.pricing_matrix === "object"
        ? course.pricing_matrix
        : {},
  };
}
/**
 * @param {{
 *   onSubmit: (payload: any) => Promise<any>;
 *   onCancel?: () => void;
 *   onUploadImage: (file: File) => Promise<string>; // uploads to your storage (GCS/Firebase), resolves to a public URL
 * }} props
 */
export default function CreateCourseAdmin({
  onSubmit,
  onUpdate,
  onDelete,
  onFetchCourses,
  onCancel,
  onUploadImage,
  onUploadGalleryImage,
  ferryRoutes = [],
  transportOptions = [],
  accommodations = [],
}) {
  // Manage is the default landing view — an admin opening "Create Course"
  // wants to see existing courses first, not a blank form. "New Course"
  // inside Manage is how they get to the form.
  const [view, setView] = useState("manage"); // "form" | "manage"
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [newFee, setNewFee] = useState("");
  const [newAmenity, setNewAmenity] = useState("");

  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [coursesError, setCoursesError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [duplicatingId, setDuplicatingId] = useState(null);

  const fetchCourses = useCallback(async () => {
    setLoadingCourses(true);
    setCoursesError(null);
    try {
      const list = await onFetchCourses();
      setCourses(list || []);
    } catch (err) {
      console.error("Failed to load courses:", err);
      setCoursesError(err?.message || "Failed to load courses.");
    } finally {
      setLoadingCourses(false);
    }
  }, [onFetchCourses]);

  useEffect(() => {
    if (view === "manage") fetchCourses();
  }, [view, fetchCourses]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const addToList = (field, value, setValue) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setField(field, [...form[field], trimmed]);
    setValue("");
  };

  const removeFromList = (field, index) => {
    setField(
      field,
      form[field].filter((_, i) => i !== index),
    );
  };

  const toggleSlot = (time) => {
    setField(
      "tee_slots",
      form.tee_slots.map((s) =>
        s.time === time ? { ...s, enabled: !s.enabled } : s,
      ),
    );
  };

  const setSlotCapacity = (time, value) => {
    setField(
      "tee_slots",
      form.tee_slots.map((s) =>
        s.time === time ? { ...s, capacity: value } : s,
      ),
    );
  };

  const setAllSlotsEnabled = (enabled) => {
    setField(
      "tee_slots",
      form.tee_slots.map((s) => ({ ...s, enabled })),
    );
  };

  const setPeriodSlotsEnabled = (period, enabled) => {
    setField(
      "tee_slots",
      form.tee_slots.map((s) => (s.period === period ? { ...s, enabled } : s)),
    );
  };

  const setAllCapacity = (capacity) => {
    setField(
      "tee_slots",
      form.tee_slots.map((s) => (s.enabled ? { ...s, capacity } : s)),
    );
  };

  const disableSlotsAfter = (cutoffTime24) => {
    setField(
      "tee_slots",
      form.tee_slots.map((s) =>
        s.time > cutoffTime24 ? { ...s, enabled: false } : s,
      ),
    );
  };

  const toggleRecommendationId = (field, id) => {
    setField(
      field,
      form[field].includes(id)
        ? form[field].filter((x) => x !== id)
        : [...form[field], id],
    );
  };

  const [newBlockedDate, setNewBlockedDate] = useState(
    /** @type {Date | undefined} */ (undefined),
  );
  const [blockedDatePickerOpen, setBlockedDatePickerOpen] = useState(false);

  // Stored as 'yyyy-MM-dd' strings — matches format(date, "yyyy-MM-dd")
  // used on the booking page, so string equality is enough to check a match.
  const addBlockedDate = (date) => {
    if (!date) return;
    const dateStr = format(date, "yyyy-MM-dd");
    if (!form.blocked_dates.includes(dateStr)) {
      setField("blocked_dates", [...form.blocked_dates, dateStr].sort());
    }
    setNewBlockedDate(undefined);
    setBlockedDatePickerOpen(false);
  };

  const removeBlockedDate = (date) => {
    setField(
      "blocked_dates",
      form.blocked_dates.filter((d) => d !== date),
    );
  };

  // Public holiday calendar — just marks which dates are holidays now; the
  // PRICE for those dates comes from the matrix's holiday_am/holiday_pm
  // columns (flat for every holiday date), not a per-date price here.
  const [newHolidayDate, setNewHolidayDate] = useState(
    /** @type {Date | undefined} */ (undefined),
  );
  const [holidayDatePickerOpen, setHolidayDatePickerOpen] = useState(false);

  const addHolidayDate = (date) => {
    if (!date) return;
    const dateStr = format(date, "yyyy-MM-dd");
    if (!form.holiday_dates.includes(dateStr)) {
      setField("holiday_dates", [...form.holiday_dates, dateStr].sort());
    }
    setNewHolidayDate(undefined);
  };

  const removeHolidayDate = (date) => {
    setField(
      "holiday_dates",
      form.holiday_dates.filter((d) => d !== date),
    );
  };

  const resetForm = () => {
    setForm(createEmptyForm());
    setErrors({});
    setTouched({});
    setNewFee("");
    setNewAmenity("");
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const startCreate = () => {
    setEditingId(null);
    resetForm();
    setView("form");
  };

  const startEdit = (course) => {
    setEditingId(course.id);
    setForm(courseToForm(course));
    setErrors({});
    setTouched({});
    setSubmitError(null);
    setSubmitSuccess(false);
    setView("form");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await onDelete(deleteTarget.id, deleteTarget.image);
      setCourses((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete course:", err);
      setCoursesError(err?.message || "Failed to delete course.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const validationErrors = validateCourseForm(form);
    setErrors(validationErrors);
    setTouched(
      Object.keys(form).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      country: form.country.trim(),
      description: form.description.trim(),
      price: Number(form.price),
      rating: form.rating === "" ? null : Number(form.rating),
      difficulty: form.difficulty === "" ? null : form.difficulty,
      blocked_advance: form.blocked_advance,
      image: form.image.trim(),
      images: form.images,
      fees_included: form.fees_included.map((f) => f.trim()).filter(Boolean),
      amenities: form.amenities.map((a) => a.trim()).filter(Boolean),
      tee_slots: form.tee_slots.map((s) => ({
        time: s.time,
        enabled: s.enabled,
        capacity: Number(s.capacity),
      })),
      ferry_route_ids: form.ferry_route_ids,
      transport_vehicle_ids: form.transport_vehicle_ids,
      accommodation_hotel_ids: form.accommodation_hotel_ids,
      block_other_round_times: form.block_other_round_times,
      block_dates_enabled: form.block_dates_enabled,
      blocked_dates: form.blocked_dates,
      terminal_group:
        form.terminal_group === "none" ? null : form.terminal_group,
      vehicle_free_for_one_round: form.vehicle_free_for_one_round,
      holiday_dates: form.holiday_dates,
      pricing_matrix: form.pricing_matrix,
    };

    setIsSubmitting(true);
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onSubmit(payload);
      }
      setSubmitSuccess(true);
      if (!editingId) resetForm();
    } catch (err) {
      console.error("Failed to save course:", err);
      setSubmitError(
        err?.message || "Failed to save course. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async (course) => {
    setSubmitError(null);
    setDuplicatingId(course.id);
    try {
      const { id, createdAt, updatedAt, ...rest } = course;
      const payload = stripUndefined({
        ...rest,
        name: `${course.name} (Copy)`,
      });
      await onSubmit(payload);
    } catch (err) {
      console.error("Failed to duplicate Course:", err);
      setSubmitError(
        err?.message || "Failed to duplicate Course. Please try again.",
      );
    } finally {
      setDuplicatingId(null);
    }
  };

  const fieldError = (field) =>
    touched[field] && errors[field] ? errors[field] : null;

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between w-full mb-2">
            <div className="flex items-center gap-3">
              {view === "form" && editingId && (
                <button
                  type="button"
                  onClick={() => setView("manage")}
                  className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <h1 className="text-3xl font-bold text-[#0B2421]">
                {view === "manage"
                  ? "Manage Courses"
                  : editingId
                    ? "Edit Course"
                    : "Add a Course"}
              </h1>
            </div>

            {view === "form" ? (
              <button
                type="button"
                onClick={() => setView("manage")}
                className="h-12 px-6 rounded-xl border border-[#0B2421]/20 text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors"
              >
                Manage Courses
              </button>
            ) : (
              <button
                type="button"
                onClick={startCreate}
                className="h-12 px-6 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors flex items-center gap-2"
              >
                <PackagePlus className="h-4 w-4" />
                New Course
              </button>
            )}
          </div>

          <p className="text-gray-500 mt-1">
            {view === "manage"
              ? "Edit or remove existing courses."
              : "Fields marked with an asterisk are required before this can go live."}
          </p>
        </motion.div>

        {view === "manage" ? (
          <ManageView
            courses={courses}
            loading={loadingCourses}
            error={coursesError}
            onEdit={startEdit}
            onDuplicate={handleDuplicate}
            duplicatingId={duplicatingId}
            onDeleteRequest={setDeleteTarget}
            deleteTarget={deleteTarget}
            onDeleteCancel={() => setDeleteTarget(null)}
            onDeleteConfirm={confirmDelete}
            isDeleting={isDeleting}
          />
        ) : (
          <>
            {submitSuccess && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
                {editingId
                  ? "Course updated successfully."
                  : "Course created successfully."}
              </div>
            )}

            {submitError && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            {/* Wide-screen layout: form on the left (2/3), sticky live preview on the right (1/3) */}
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <div className="lg:col-span-2 space-y-6">
                  <Section title="Basics">
                    <div className="space-y-4">
                      <Field
                        label="Course Name"
                        required
                        name="name"
                        error={fieldError("name")}
                      >
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setField("name", e.target.value)}
                          onBlur={() => markTouched("name")}
                          placeholder="e.g., Pebble Beach Golf Links"
                          className={inputClass(fieldError("name"))}
                        />
                      </Field>

                      <Field label="Address" icon={MapPin} name="address">
                        <input
                          type="text"
                          value={form.address}
                          onChange={(e) => setField("address", e.target.value)}
                          placeholder="e.g., 1700 17-Mile Drive"
                          className={inputClass(false)}
                        />
                      </Field>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                          label="City"
                          required
                          icon={MapPin}
                          name="city"
                          error={fieldError("city")}
                        >
                          <input
                            type="text"
                            value={form.city}
                            onChange={(e) => setField("city", e.target.value)}
                            onBlur={() => markTouched("city")}
                            placeholder="e.g., Pebble Beach"
                            className={inputClass(fieldError("city"))}
                          />
                        </Field>

                        <Field
                          label="Country"
                          required
                          name="country"
                          error={fieldError("country")}
                        >
                          <select
                            value={form.country}
                            onChange={(e) =>
                              setField("country", e.target.value)
                            }
                            onBlur={() => markTouched("country")}
                            className={inputClass(fieldError("country"))}
                          >
                            <option value="">Select Country</option>
                            <option value="Indonesia">Indonesia</option>
                            <option value="Thailand">Thailand</option>
                            <option value="Vietnam">Vietnam</option>
                            <option value="Malaysia">Malaysia</option>
                          </select>
                        </Field>
                      </div>

                      <Field
                        label="About This Course"
                        required
                        name="description"
                        error={fieldError("description")}
                      >
                        <textarea
                          value={form.description}
                          onChange={(e) =>
                            setField("description", e.target.value)
                          }
                          onBlur={() => markTouched("description")}
                          rows={4}
                          placeholder="What makes this course worth booking?"
                          className={`${inputClass(fieldError("description"))} h-auto py-3 resize-none`}
                        />
                        <div className="flex justify-end mt-1">
                          <span className="text-xs text-gray-400">
                            {form.description.length}/2000
                          </span>
                        </div>
                      </Field>

                      <Field
                        label="Course Friendliness"
                        name="difficulty"
                        error={fieldError("difficulty")}
                      >
                        <textarea
                          value={form.difficulty}
                          onChange={(e) =>
                            setField("difficulty", e.target.value)
                          }
                          onBlur={() => markTouched("difficulty")}
                          rows={3}
                          placeholder="e.g., Wide, forgiving fairways and gentle greens make this a relaxed round for golfers of any level."
                          className={`${inputClass(fieldError("difficulty"))} h-auto py-3 resize-none`}
                        />
                      </Field>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                          label="Price per Player (SGD)"
                          required
                          name="price"
                          error={fieldError("price")}
                        >
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37] font-semibold">
                              SGD
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.price}
                              onChange={(e) =>
                                setField("price", e.target.value)
                              }
                              onBlur={() => markTouched("price")}
                              placeholder="150"
                              className={`${inputClass(fieldError("price"))} pl-16`}
                            />
                          </div>
                        </Field>

                        <Field
                          label="Rating (0–5, optional)"
                          icon={Star}
                          name="rating"
                          error={fieldError("rating")}
                        >
                          <input
                            type="number"
                            min="0"
                            max="5"
                            step="0.1"
                            value={form.rating}
                            onChange={(e) => setField("rating", e.target.value)}
                            onBlur={() => markTouched("rating")}
                            placeholder="4.5"
                            className={inputClass(fieldError("rating"))}
                          />
                        </Field>
                      </div>
                    </div>
                  </Section>

                  <Section title="Images" icon={ImageIcon}>
                    <div className="mb-6" data-field="image">
                      <FieldLabel required>Main Photo</FieldLabel>
                      <MainImageUpload
                        value={form.image}
                        onChange={(url) => setField("image", url)}
                        onBlur={() => markTouched("image")}
                        onUploadImage={onUploadImage}
                        error={fieldError("image")}
                      />
                    </div>

                    <div>
                      <FieldLabel>Gallery Photos (upto 20)</FieldLabel>
                      <GalleryUpload
                        items={form.images}
                        onAdd={(urls) =>
                          setField("images", [...form.images, ...urls])
                        }
                        onRemove={(i) => removeFromList("images", i)}
                        onUploadImage={onUploadGalleryImage}
                      />
                    </div>
                  </Section>

                  <Section title="Fees & Amenities">
                    <div className="mb-6" data-field="fees_included">
                      <FieldLabel>Included in Fees (optional)</FieldLabel>
                      <ChipListEditor
                        items={form.fees_included}
                        inputValue={newFee}
                        setInputValue={setNewFee}
                        onAdd={() =>
                          addToList("fees_included", newFee, setNewFee)
                        }
                        onRemove={(i) => removeFromList("fees_included", i)}
                        placeholder="e.g., Cart rental"
                      />
                      {fieldError("fees_included") && (
                        <FieldError message={errors.fees_included} />
                      )}
                    </div>

                    <div data-field="amenities">
                      <FieldLabel>Amenities & Features.</FieldLabel>
                      <ChipListEditor
                        items={form.amenities}
                        inputValue={newAmenity}
                        setInputValue={setNewAmenity}
                        onAdd={() =>
                          addToList("amenities", newAmenity, setNewAmenity)
                        }
                        onRemove={(i) => removeFromList("amenities", i)}
                        placeholder="e.g., Pro shop"
                      />
                      {fieldError("amenities") && (
                        <FieldError message={errors.amenities} />
                      )}
                    </div>
                  </Section>

                  <Section
                    title="Tee Time Availability & Capacity"
                    icon={Clock}
                  >
                    <div data-field="tee_slots">
                      <p className="text-sm text-gray-500 mb-1">
                        Slots run in fixed 15-minute intervals platform-wide —
                        7:00 AM–11:45 AM and 12:00 PM–2:30 PM (31 slots/day).
                        This structure isn't editable; toggle individual slots
                        or use bulk edit below. At least one slot must stay
                        enabled.
                      </p>
                      {fieldError("tee_slots") && (
                        <FieldError message={errors.tee_slots} />
                      )}
                      <div className="mt-4">
                        <TeeTimeSlotGrid
                          slots={form.tee_slots}
                          onToggle={toggleSlot}
                          onCapacityChange={setSlotCapacity}
                          onBulkAllEnabled={setAllSlotsEnabled}
                          onBulkPeriod={setPeriodSlotsEnabled}
                          onBulkCapacity={setAllCapacity}
                          onBulkDisableAfter={disableSlotsAfter}
                        />
                      </div>
                    </div>

                    {/* New: round-based time locking toggle */}
                    <div className="mt-5 pt-5 border-t border-gray-100">
                      <Field label="Block Same Time Across Rounds">
                        <div className="flex rounded-xl border border-gray-200 bg-gray-50 p-1 w-fit">
                          {[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                          ].map((opt) => (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() =>
                                setField("block_other_round_times", opt.value)
                              }
                              className={`h-9 px-5 rounded-lg text-sm font-semibold transition-colors ${
                                form.block_other_round_times === opt.value
                                  ? "bg-[#0B2421] text-white"
                                  : "text-gray-500 hover:bg-gray-100"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <p className="mt-1.5 text-xs text-gray-400 flex items-start gap-1.5">
                        <Lock className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        When enabled, a golfer booking a time slot for one round
                        on this course automatically locks that same time slot
                        for their other rounds. Enforcement happens server-side
                        at booking time — this only sets the flag for this
                        course.
                      </p>
                    </div>
                  </Section>

                  <Section title="Block Specific Dates" icon={Lock}>
                    <div className="mb-5">
                      <Field label="Enable Date Blocking">
                        <div className="flex rounded-xl border border-gray-200 bg-gray-50 p-1 w-fit">
                          {[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                          ].map((opt) => (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() =>
                                setField("block_dates_enabled", opt.value)
                              }
                              className={`h-9 px-5 rounded-lg text-sm font-semibold transition-colors ${
                                form.block_dates_enabled === opt.value
                                  ? "bg-[#0B2421] text-white"
                                  : "text-gray-500 hover:bg-gray-100"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <p className="mt-1.5 text-xs text-gray-400">
                        When enabled, this course won't be shown to golfers for
                        any round whose selected date matches one of the blocked
                        dates below.
                      </p>
                    </div>

                    {form.block_dates_enabled && (
                      <div>
                        <FieldLabel>Blocked Dates</FieldLabel>
                        <div className="flex gap-2 mb-3">
                          <Popover
                            open={blockedDatePickerOpen}
                            onOpenChange={setBlockedDatePickerOpen}
                          >
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 flex items-center gap-2 text-left text-gray-700 hover:border-[#0B2421] transition-colors"
                              >
                                <CalendarIcon className="h-4 w-4 text-[#D4AF37] shrink-0" />
                                <span
                                  className={
                                    newBlockedDate
                                      ? "text-gray-700"
                                      : "text-gray-400"
                                  }
                                >
                                  {newBlockedDate
                                    ? format(newBlockedDate, "d MMM yyyy")
                                    : "Pick a date to block"}
                                </span>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                              sideOffset={8}
                            >
                              <Calendar
                                mode="single"
                                selected={newBlockedDate}
                                onSelect={(d) => addBlockedDate(d)}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        {form.blocked_dates.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {form.blocked_dates.map((date) => (
                              <span
                                key={date}
                                className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-100 px-3 py-1.5 text-sm text-red-700"
                              >
                                {format(
                                  new Date(`${date}T00:00:00`),
                                  "d MMM yyyy",
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeBlockedDate(date)}
                                  className="text-red-400 hover:text-red-600 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">
                            No dates blocked yet.
                          </p>
                        )}
                      </div>
                    )}

                    <Field
                      label="Advanced Booking Period (hours)"
                      name="price"
                      error={fieldError("price")}
                    >
                      <div className="relative">
                        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[#D4AF37] font-semibold">
                          hours
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.blocked_advance}
                          onChange={(e) =>
                            setField("blocked_advance", e.target.value)
                          }
                          onBlur={() => markTouched("blocked_advance")}
                          placeholder="Days in Hours"
                          className={`${inputClass(fieldError("blocked_advance"))} pl-20`}
                        />
                      </div>
                    </Field>
                  </Section>

                  <Section title="Vehicle Pricing" icon={Lock}>
                    <div>
                      <Field label="Vehicle Free for One Round">
                        <div className="flex rounded-xl border border-gray-200 bg-gray-50 p-1 w-fit">
                          {[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                          ].map((opt) => (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() =>
                                setField(
                                  "vehicle_free_for_one_round",
                                  opt.value,
                                )
                              }
                              className={`h-9 px-5 rounded-lg text-sm font-semibold transition-colors ${
                                form.vehicle_free_for_one_round === opt.value
                                  ? "bg-[#0B2421] text-white"
                                  : "text-gray-500 hover:bg-gray-100"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </Field>
                      <p className="mt-1.5 text-xs text-gray-400">
                        When "Yes", the vehicle is not chargeable — and won't
                        be shown to the golfer at all — for a one-round
                        booking on this course. Vehicle pricing applies as
                        normal for 2+ rounds.
                      </p>
                    </div>
                  </Section>

                  <Section title="Public Holiday Dates" icon={Lock}>
                    <p className="text-sm text-gray-500 mb-3">
                      Mark which dates are public holidays — the price for
                      those dates is set in the Round Pricing grid below
                      (the "Public Holiday AM/PM" tabs), applied to every
                      date listed here.
                    </p>
                    <div className="flex gap-2 mb-3">
                      <Popover
                        open={holidayDatePickerOpen}
                        onOpenChange={setHolidayDatePickerOpen}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 flex items-center gap-2 text-left text-gray-700 hover:border-[#0B2421] transition-colors"
                          >
                            <CalendarIcon className="h-4 w-4 text-[#D4AF37] shrink-0" />
                            <span
                              className={
                                newHolidayDate ? "text-gray-700" : "text-gray-400"
                              }
                            >
                              {newHolidayDate
                                ? format(newHolidayDate, "d MMM yyyy")
                                : "Pick a public holiday date"}
                            </span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0"
                          align="start"
                          sideOffset={8}
                        >
                          <Calendar
                            mode="single"
                            selected={newHolidayDate}
                            onSelect={(d) => addHolidayDate(d)}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {form.holiday_dates.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {form.holiday_dates.map((date) => (
                          <span
                            key={date}
                            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-100 px-3 py-1.5 text-sm text-amber-700"
                          >
                            {format(new Date(`${date}T00:00:00`), "d MMM yyyy")}
                            <button
                              type="button"
                              onClick={() => removeHolidayDate(date)}
                              className="text-amber-400 hover:text-amber-600 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">
                        No public holiday dates set yet.
                      </p>
                    )}
                  </Section>

                  <Section title="Round Pricing" icon={Lock}>
                    <p className="text-sm text-gray-500 mb-4">
                      Per-player round price by day type, number of players,
                      and total rounds in the booking. The same rate applies
                      to every round once the total round count picks a
                      column — e.g. a 3-round booking prices all 3 rounds
                      from the "3 Rounds or more" column.
                    </p>
                    <MatrixPriceEditor
                      dayTypes={COURSE_DAY_TYPES}
                      rows={[
                        { key: "p1to2", label: "1 to 2 players" },
                        { key: "p3plus", label: "3 or more players" },
                      ]}
                      columns={[
                        { key: "r1", label: "1 Round" },
                        { key: "r2", label: "2 Rounds" },
                        { key: "r3plus", label: "3 Rounds or more" },
                      ]}
                      value={form.pricing_matrix}
                      onChange={(m) => setField("pricing_matrix", m)}
                    />
                  </Section>

                  <Section
                    title="Terminal Group (Round 1 Tee Time)"
                    icon={Lock}
                  >
                    <p className="text-sm text-gray-500 mb-4">
                      If this course is tied to a specific ferry terminal, tee
                      times before that terminal's earliest feasible time will
                      be blocked — but only for Round 1 (later rounds aren't
                      affected by ferry arrival).
                    </p>
                    <Field label="Terminal Group">
                      <select
                        value={form.terminal_group}
                        onChange={(e) =>
                          setField("terminal_group", e.target.value)
                        }
                        className={inputClass(false)}
                      >
                        {TERMINAL_GROUPS.map((g) => (
                          <option key={g.value} value={g.value}>
                            {g.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </Section>

                  <Section title="Recommended Travel Options">
                    <p className="text-sm text-gray-500 mb-5">
                      Pick the ferry routes, land transport, and hotels that
                      should be recommended to golfers who book this course.
                      These selections drive the "Recommended" cards shown on
                      the booking page.
                    </p>

                    <div className="space-y-8">
                      <RecommendationPicker
                        icon={Ship}
                        title="Ferry Routes"
                        items={ferryRoutes}
                        getId={(f) => f.id}
                        getName={(f) => f.title}
                        getSubtitle={(f) =>
                          [f.originport, f.destination]
                            .filter(Boolean)
                            .join(" ⇄ ")
                        }
                        getImage={(f) => f.image}
                        getMeta={(f) =>
                          f.price != null ? `SGD ${f.price}/passenger` : null
                        }
                        selectedIds={form.ferry_route_ids}
                        onToggle={(id) =>
                          toggleRecommendationId("ferry_route_ids", id)
                        }
                      />

                      <RecommendationPicker
                        icon={Car}
                        title="Land Transport"
                        items={transportOptions}
                        getId={(t) => t.id}
                        getName={(t) =>
                          [t.title, t.type].filter(Boolean).join(" · ")
                        }
                        getSubtitle={(t) =>
                          [t.originport, t.destination]
                            .filter(Boolean)
                            .join(" ⇄ ")
                        }
                        getImage={(t) => t.image}
                        getMeta={(t) =>
                          t.price != null ? `SGD ${t.price}` : null
                        }
                        selectedIds={form.transport_vehicle_ids}
                        onToggle={(id) =>
                          toggleRecommendationId("transport_vehicle_ids", id)
                        }
                      />

                      <RecommendationPicker
                        icon={Bed}
                        title="Accommodation"
                        items={accommodations}
                        getId={(a) => a.id}
                        getName={(a) => a.title}
                        getSubtitle={(a) => a.destination}
                        getImage={(a) => a.image}
                        getMeta={(a) =>
                          a.price != null ? `SGD ${a.price}/night` : null
                        }
                        selectedIds={form.accommodation_hotel_ids}
                        onToggle={(id) =>
                          toggleRecommendationId("accommodation_hotel_ids", id)
                        }
                      />
                    </div>
                  </Section>

                  <div className="flex items-center gap-3 justify-end pb-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="h-12 px-8 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {editingId ? "Saving..." : "Creating..."}
                        </>
                      ) : editingId ? (
                        "Save Changes"
                      ) : (
                        "Create Course"
                      )}
                    </button>
                  </div>
                </div>

                {/* Live preview — only really useful once there's room for it, hence lg-only */}
                <div className="hidden lg:block lg:sticky lg:top-8">
                  <LivePreview form={form} />
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ManageView({
  courses,
  loading,
  error,
  onEdit,
  onDuplicate,
  duplicatingId,
  onDeleteRequest,
  deleteTarget,
  onDeleteCancel,
  onDeleteConfirm,
  isDeleting,
}) {
  const {
    items: orderedCourses,
    getDragHandlers,
    isDragging,
    isDragOver,
  } = useDragReorder(
    courses,
    (c) => c.id,
    (ids) => saveItemOrder("course", ids),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading courses...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
        <PackagePlus className="h-8 w-8 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No courses yet.</p>
      </div>
    );
  }

  return (
    <>
      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5" />
        Drag a card to reorder — this is the order shown to golfers.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {orderedCourses.map((course) => (
          <div
            key={course.id}
            {...getDragHandlers(course)}
            className={`rounded-2xl border bg-white shadow-sm overflow-hidden group cursor-grab active:cursor-grabbing transition-opacity ${
              isDragging(course)
                ? "opacity-40 border-gray-100"
                : isDragOver(course)
                  ? "border-[#0B2421] ring-2 ring-[#0B2421]/30"
                  : "border-gray-100"
            }`}
          >
            <div className="relative h-36 bg-gray-100">
              <div className="absolute top-2 left-2 z-10 h-6 w-6 rounded-md bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80">
                <GripVertical className="h-3.5 w-3.5" />
              </div>
              {course.image ? (
                <img
                  src={course.image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => onEdit(course)}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>

                <button
                  type="button"
                  onClick={() => onDuplicate(course)}
                  disabled={duplicatingId === course.id}
                  className="h-9 px-4 rounded-lg bg-white text-[#0B2421] text-sm font-semibold flex items-center gap-1.5 disabled:opacity-40"
                >
                  {duplicatingId === course.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  Duplicate
                </button>

                <button
                  type="button"
                  onClick={() => onDeleteRequest(course)}
                  className="h-9 px-4 rounded-lg bg-white/90 text-red-600 text-sm font-semibold flex items-center gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </div>
            <div className="p-4">
              {(course.city || course.country) && (
                <p className="text-[10px] font-semibold uppercase tracking-widest text-[#D4AF37] mb-0.5">
                  {[course.city, course.country].filter(Boolean).join(", ")}
                </p>
              )}
              <h3 className="text-sm font-bold text-[#0B2421] truncate">
                {course.name}
              </h3>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 truncate pr-2">
                  {course.rating != null ? `★ ${course.rating}` : ""}
                </span>
                {course.price != null && (
                  <span className="text-sm font-bold text-[#0B2421] flex-shrink-0">
                    SGD {course.price}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={onDeleteCancel}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-[#0B2421] mb-2">
                Delete course?
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                This will permanently remove "{deleteTarget.name}" and its main
                photo. This can't be undone.
              </p>
              <div className="flex items-center gap-3 justify-end">
                <button
                  type="button"
                  onClick={onDeleteCancel}
                  disabled={isDeleting}
                  className="h-10 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onDeleteConfirm}
                  disabled={isDeleting}
                  className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ---------- layout primitives ---------- */

function Section({ title, icon: Icon, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm"
    >
      <h2 className="text-lg font-bold mb-4 text-[#0B2421] flex items-center">
        {Icon && <Icon className="h-5 w-5 mr-2" />}
        {title}
      </h2>
      {children}
    </motion.div>
  );
}

function FieldLabel({ children, required, icon: Icon }) {
  return (
    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
      {Icon && <Icon className="inline h-4 w-4 mr-1" />}
      {children} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
  );
}

function Field({ label, required, icon, error, name, children }) {
  return (
    <div data-field={name}>
      <FieldLabel required={required} icon={icon}>
        {label}
      </FieldLabel>
      {children}
      {error && <FieldError message={error} />}
    </div>
  );
}

function inputClass(hasError) {
  return `w-full h-12 rounded-xl border px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 ${
    hasError ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
  }`;
}

function FieldError({ message }) {
  return (
    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />
      {message}
    </p>
  );
}

/* ---------- image upload ---------- */

function MainImageUpload({ value, onChange, onBlur, onUploadImage, error }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (!file || !isImageFile(file)) {
        setLocalError("Please choose an image file.");
        return;
      }
      setLocalError(null);
      setIsUploading(true);
      try {
        const url = await onUploadImage(file);
        onChange(url);
      } catch (err) {
        console.error("Image upload failed:", err);
        setLocalError(err?.message || "Upload failed. Try again.");
      } finally {
        setIsUploading(false);
        onBlur?.();
      }
    },
    [onUploadImage, onChange, onBlur],
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (value && !isUploading) {
    return (
      <div className="relative group rounded-xl overflow-hidden border border-gray-200 h-48">
        <img
          src={value} 
          alt="Course preview"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={() =>
              downloadImage(value, filenameFromUrl(value, "main-photo.jpg"))
            }
            className="px-4 py-2 rounded-lg bg-white/90 text-green-600 text-sm font-semibold"
          >
            Download
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="px-4 py-2 rounded-lg bg-white text-[#0B2421] text-sm font-semibold"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            className="px-4 py-2 rounded-lg bg-white/90 text-red-600 text-sm font-semibold"
          >
            Remove
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center h-48 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
          isDragging
            ? "border-[#0B2421] bg-[#0B2421]/5"
            : error || localError
              ? "border-red-300 bg-red-50"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-6 w-6 text-[#0B2421] animate-spin mb-2" />
            <span className="text-sm text-gray-500">Uploading...</span>
          </>
        ) : (
          <>
            <UploadCloud className="h-6 w-6 text-gray-400 mb-2" />
            <span className="text-sm font-medium text-gray-600">
              Click to upload or drag a photo here
            </span>
            <span className="text-xs text-gray-400 mt-1">PNG or JPG</span>
          </>
        )}
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
      {(error || localError) && <FieldError message={error || localError} />}
    </div>
  );
}

function GalleryUpload({ items, onAdd, onRemove, onUploadImage }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter((f) =>
        isImageFile(f),
      );
      if (files.length === 0) return;
      setError(null);
      setIsUploading(true);
      try {
        const urls = await Promise.all(
          files.map((file) => onUploadImage(file)),
        );
        onAdd(urls); // pass the whole array, single call
      } catch (err) {
        console.error("Gallery upload failed:", err);
        setError(err?.message || "One or more uploads failed.");
      } finally {
        setIsUploading(false);
      }
    },
    [onUploadImage, onAdd],
  );

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {items.map((url, index) => (
          <div
            key={`${url}-${index}`}
            className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 group"
          >
            <img src={url} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-1 right-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() =>
                  downloadImage(
                    url,
                    filenameFromUrl(url, `gallery-${index + 1}.jpg`),
                  )
                }
                className="h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Download image"
                title="Download image"
              >
                <Download className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                aria-label="Remove image"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}

        <label
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`w-24 h-24 flex-shrink-0 flex flex-col items-center justify-center rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
            isDragging
              ? "border-[#0B2421] bg-[#0B2421]/5"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
          }`}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 text-[#0B2421] animate-spin" />
          ) : (
            <>
              <Plus className="h-4 w-4 text-gray-400" />
              <span className="text-[10px] text-gray-400 mt-1">Add</span>
            </>
          )}
          <input
            type="file"
            accept={IMAGE_ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>
      </div>
      {error && <FieldError message={error} />}
    </div>
  );
}

function ChipListEditor({
  items,
  inputValue,
  setInputValue,
  onAdd,
  onRemove,
  placeholder,
}) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 h-11 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
        />
        <button
          type="button"
          onClick={onAdd}
          className="h-11 w-11 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white flex items-center justify-center flex-shrink-0 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm text-gray-700"
            >
              {item}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- tee time slot grid ---------- */

const bulkBtnClass =
  "h-8 px-3 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-[#0B2421] hover:bg-gray-100 transition-colors";

function TeeTimeSlotGrid({
  slots,
  onToggle,
  onCapacityChange,
  onBulkAllEnabled,
  onBulkPeriod,
  onBulkCapacity,
  onBulkDisableAfter,
}) {
  const [bulkCapacity, setBulkCapacity] = useState(String(DEFAULT_CAPACITY));
  const [cutoffTime, setCutoffTime] = useState("13:00");
  // Collapsed by default — 31 rows is a lot to render just to land on the
  // page. Admins normally only need the bulk-edit tools; individual slot
  // toggling/capacity is an "expand for detail" action.
  const [showAllSlots, setShowAllSlots] = useState(false);

  const morning = slots.filter((s) => s.period === "AM");
  const afternoon = slots.filter((s) => s.period === "PM");
  const enabledSlots = slots.filter((s) => s.enabled);
  const totalCapacity = enabledSlots.reduce(
    (sum, s) => sum + (Number(s.capacity) || 0),
    0,
  );

  return (
    <div>
      {/* Bulk edit affordance — necessary given 31 rows per course */}
      <div className="mb-5 p-4 rounded-xl bg-gray-50 border border-gray-100">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-[#0B2421]">
          <SlidersHorizontal className="h-4 w-4" />
          Bulk edit
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            onClick={() => onBulkAllEnabled(true)}
            className={bulkBtnClass}
          >
            Enable all
          </button>
          <button
            type="button"
            onClick={() => onBulkAllEnabled(false)}
            className={bulkBtnClass}
          >
            Disable all
          </button>
          <button
            type="button"
            onClick={() => onBulkPeriod("AM", true)}
            className={bulkBtnClass}
          >
            Enable AM
          </button>
          <button
            type="button"
            onClick={() => onBulkPeriod("AM", false)}
            className={bulkBtnClass}
          >
            Disable AM
          </button>
          <button
            type="button"
            onClick={() => onBulkPeriod("PM", true)}
            className={bulkBtnClass}
          >
            Enable PM
          </button>
          <button
            type="button"
            onClick={() => onBulkPeriod("PM", false)}
            className={bulkBtnClass}
          >
            Disable PM
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-gray-500">
            Set capacity for all enabled slots to
          </span>
          <input
            type="number"
            min="1"
            value={bulkCapacity}
            onChange={(e) => setBulkCapacity(e.target.value)}
            className="w-20 h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
          />
          <button
            type="button"
            onClick={() => onBulkCapacity(bulkCapacity)}
            className={bulkBtnClass}
          >
            Apply
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">
            Disable every slot after
          </span>
          <input
            type="time"
            value={cutoffTime}
            onChange={(e) => setCutoffTime(e.target.value)}
            className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
          />
          <button
            type="button"
            onClick={() => onBulkDisableAfter(cutoffTime)}
            className={bulkBtnClass}
          >
            Apply
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowAllSlots((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-[#0B2421]">
          {enabledSlots.length} of {slots.length} slots enabled · total daily
          capacity {totalCapacity} players
        </span>
        <span className="flex items-center gap-1 text-xs font-semibold text-[#0B2421]">
          {showAllSlots
            ? "Hide individual slots"
            : "View & edit individual slots"}
          {showAllSlots ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </span>
      </button>

      {showAllSlots && (
        <div className="mt-4">
          <SlotPeriodBlock
            title="Morning"
            icon={Sun}
            slots={morning}
            onToggle={onToggle}
            onCapacityChange={onCapacityChange}
          />
          <div className="h-5" />
          <SlotPeriodBlock
            title="Afternoon"
            icon={Sunset}
            slots={afternoon}
            onToggle={onToggle}
            onCapacityChange={onCapacityChange}
          />
        </div>
      )}
    </div>
  );
}

function SlotPeriodBlock({
  title,
  icon: Icon,
  slots,
  onToggle,
  onCapacityChange,
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-500 flex items-center gap-1.5 mb-2">
        <Icon className="h-4 w-4" />
        {title}
        <span className="text-gray-300 font-normal">
          ({slots.length} slots)
        </span>
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
        {slots.map((slot) => (
          <SlotCard
            key={slot.time}
            slot={slot}
            onToggle={onToggle}
            onCapacityChange={onCapacityChange}
          />
        ))}
      </div>
    </div>
  );
}

function SlotCard({ slot, onToggle, onCapacityChange }) {
  return (
    <div
      className={`rounded-xl border p-3 flex flex-col gap-2 transition-colors ${
        slot.enabled ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`text-sm font-semibold ${
            slot.enabled ? "text-[#0B2421]" : "text-gray-400 line-through"
          }`}
        >
          {slot.label}
        </span>
        <button
          type="button"
          onClick={() => onToggle(slot.time)}
          aria-label={slot.enabled ? "Disable slot" : "Enable slot"}
          className={slot.enabled ? "text-[#0B2421]" : "text-gray-300"}
        >
          {slot.enabled ? (
            <ToggleRight className="h-6 w-6" />
          ) : (
            <ToggleLeft className="h-6 w-6" />
          )}
        </button>
      </div>
      <div className="relative">
        <Users className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          type="number"
          min="1"
          value={slot.capacity}
          disabled={!slot.enabled}
          onChange={(e) => onCapacityChange(slot.time, e.target.value)}
          className="w-full h-9 rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 disabled:opacity-40 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}

/* ---------- recommended travel-option pickers ---------- */

// Default number of cards shown before "View all" — chosen to match the
// widest grid (lg:grid-cols-4), so nothing looks like a half-empty row on
// desktop; on smaller screens it just wraps to 2–3 per row.
const DEFAULT_VISIBLE_RECOMMENDATIONS = 4;

function RecommendationPicker({
  icon: Icon,
  title,
  items,
  getId,
  getName,
  getSubtitle,
  getImage,
  getMeta,
  selectedIds,
  onToggle,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Autofocus the search box the moment the dropdown opens
  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus();
  }, [isOpen]);

  if (!items?.length) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="h-4 w-4 text-[#0B2421]" />
          <h3 className="text-sm font-bold text-[#0B2421]">{title}</h3>
        </div>
        <p className="text-xs text-gray-400">No options available yet.</p>
      </div>
    );
  }

  const selectedItems = items.filter((item) =>
    selectedIds.includes(getId(item)),
  );

  const query = search.trim().toLowerCase();
  const filteredItems = query
    ? items.filter((item) => {
        const name = getName(item)?.toLowerCase() || "";
        const subtitle = getSubtitle?.(item)?.toLowerCase() || "";
        return name.includes(query) || subtitle.includes(query);
      })
    : items;

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#0B2421]" />
          <h3 className="text-sm font-bold text-[#0B2421]">{title}</h3>
        </div>
        <span className="text-xs text-gray-400">
          {selectedIds.length} selected
        </span>
      </div>

      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`w-full flex items-center justify-between h-12 rounded-xl border px-4 text-left transition-colors ${
          isOpen
            ? "border-[#0B2421] ring-2 ring-[#0B2421]/20"
            : "border-gray-200 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <span className="text-sm text-gray-600 truncate">
          {selectedIds.length === 0
            ? `Select ${title.toLowerCase()}...`
            : selectedItems.map((item) => getName(item)).join(", ")}
        </span>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
        )}
      </button>

      {/* Selected chips, shown below the trigger so choices stay visible
          without needing the dropdown open */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedItems.map((item) => {
            const id = getId(item);
            const subtitle = getSubtitle?.(item);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0B2421]/5 border border-[#0B2421]/15 px-2.5 py-1 text-xs text-[#0B2421] font-medium"
              >
                {getName(item)}
                {subtitle && (
                  <span className="text-[#0B2421]/50 font-normal">
                    · {subtitle}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onToggle(id)}
                  className="text-[#0B2421]/50 hover:text-red-500 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${title.toLowerCase()}...`}
                className="w-full h-9 rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-gray-400">
                No matches for "{search}".
              </p>
            ) : (
              filteredItems.map((item) => {
                const id = getId(item);
                const isSelected = selectedIds.includes(id);
                const image = getImage?.(item);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onToggle(id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                      isSelected ? "bg-[#0B2421]/5" : "hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 h-4 w-4 rounded border flex items-center justify-center ${
                        isSelected
                          ? "bg-[#0B2421] border-[#0B2421]"
                          : "border-gray-300"
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>

                    {image ? (
                      <div className="h-9 w-9 rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-9 w-9 rounded-md bg-gray-50 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-4 w-4 text-gray-300" />
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-[#0B2421] truncate">
                        {getName(item)}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate">
                        {getSubtitle?.(item)}
                      </p>
                    </div>

                    {getMeta && (
                      <span className="text-[11px] text-gray-500 flex-shrink-0">
                        {getMeta(item)}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- live preview (large screens only) ---------- */

function LivePreview({ form }) {
  const hasContent = form.name || form.image || form.city;
  const enabledSlotCount = form.tee_slots.filter((s) => s.enabled).length;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-[#0B2421]">
        <span className="text-xs font-semibold tracking-wide text-[#D4AF37] uppercase">
          Preview
        </span>
      </div>

      {!hasContent ? (
        <div className="p-8 text-center text-sm text-gray-400">
          Fill in the form to see how this course will appear to golfers.
        </div>
      ) : (
        <div>
          <div className="h-40 bg-gray-100">
            {form.image ? (
              <img
                src={form.image}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
          </div>
          <div className="p-5">
            <h3 className="font-bold text-[#0B2421] text-lg leading-snug">
              {form.name || "Course name"}
            </h3>
            {(form.address || form.city || form.country) && (
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <MapPin className="h-3.5 w-3.5" />
                {[form.address, form.city, form.country]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}

            <div className="flex items-center gap-3 mt-3">
              {form.price && (
                <span className="text-[#0B2421] font-semibold">
                  SGD {form.price}
                  <span className="text-gray-400 font-normal text-sm">
                    {" "}
                    Per Person
                  </span>
                </span>
              )}
              {form.rating && (
                <span className="flex items-center gap-1 text-sm text-gray-600">
                  <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                  {form.rating}
                </span>
              )}
            </div>

            {form.description && (
              <p className="text-sm text-gray-500 mt-3 line-clamp-3">
                {form.description}
              </p>
            )}

            {form.amenities.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {form.amenities.slice(0, 4).map((a, i) => (
                  <span
                    key={i}
                    className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600"
                  >
                    {a}
                  </span>
                ))}
                {form.amenities.length > 4 && (
                  <span className="text-xs px-2 py-1 text-gray-400">
                    +{form.amenities.length - 4} more
                  </span>
                )}
              </div>
            )}

            {form.tee_slots.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {enabledSlotCount} of {form.tee_slots.length} tee time slots
                  available
                </span>
              </div>
            )}

            {(form.ferry_route_ids.length > 0 ||
              form.transport_vehicle_ids.length > 0 ||
              form.accommodation_hotel_ids.length > 0) && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-3 text-xs text-gray-400">
                <span>{form.ferry_route_ids.length} ferry route(s)</span>
                <span>
                  {form.transport_vehicle_ids.length} transport option(s)
                </span>
                <span>{form.accommodation_hotel_ids.length} hotel(s)</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}